import { useState, useEffect, useCallback } from 'react'
import type { Location } from '../types'

interface GeolocationState {
  location: Location | null
  error: string | null
  loading: boolean
  permissionDenied: boolean
}

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean
  timeout?: number
  maximumAge?: number
  watch?: boolean
}

export function useGeolocation(options: UseGeolocationOptions = {}) {
  const {
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 0,
    watch = false,
  } = options

  const [state, setState] = useState<GeolocationState>({
    location: null,
    error: null,
    loading: watch,  // Start loading immediately if watch mode
    permissionDenied: false,
  })

  const handleSuccess = useCallback((position: GeolocationPosition) => {
    setState({
      location: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      },
      error: null,
      loading: false,
      permissionDenied: false,
    })
  }, [])

  const handleError = useCallback((error: GeolocationPositionError) => {
    let errorMessage = 'Unable to get location'

    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location permission denied'
        setState((prev) => ({
          ...prev,
          error: errorMessage,
          loading: false,
          permissionDenied: true,
        }))
        return
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location unavailable'
        break
      case error.TIMEOUT:
        errorMessage = 'Location request timed out'
        break
    }

    // For temporary errors (timeout, unavailable), keep loading true
    // so we show "acquiring signal" not "enable location"
    setState((prev) => ({
      ...prev,
      error: errorMessage,
      loading: true,  // Keep loading - we're still trying
      permissionDenied: false,
    }))
  }, [])

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState({
        location: null,
        error: 'Geolocation not supported',
        loading: false,
        permissionDenied: false,
      })
      return
    }

    setState((prev) => ({ ...prev, loading: true, error: null }))

    const geoOptions: PositionOptions = {
      enableHighAccuracy,
      timeout,
      maximumAge,
    }

    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, geoOptions)
  }, [enableHighAccuracy, timeout, maximumAge, handleSuccess, handleError])

  // Watch mode for continuous updates
  useEffect(() => {
    if (!watch || !navigator.geolocation) return

    setState((prev) => ({ ...prev, loading: true }))

    const geoOptions: PositionOptions = {
      enableHighAccuracy,
      timeout,
      maximumAge,
    }

    const watchId = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      geoOptions
    )

    return () => {
      navigator.geolocation.clearWatch(watchId)
    }
  }, [watch, enableHighAccuracy, timeout, maximumAge, handleSuccess, handleError])

  return {
    ...state,
    requestLocation,
  }
}

// Helper to calculate distance between two points (Haversine formula)
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000 // Earth's radius in meters
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180)
}

// Format distance for display
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`
  }
  return `${(meters / 1000).toFixed(1)}km`
}
